import React, { useState, useMemo } from 'react'
import { motion } from 'framer-motion'
import SEO from '../components/SEO'
import QRCodeModal from '../components/QRCodeModal'
import { useQRModal } from '../hooks'
import servicesData from '../data/services.json'

const ServicesPageV2: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('All')
  const [expandedServices, setExpandedServices] = useState<Set<string>>(new Set())
  const { isOpen, openModal, closeModal } = useQRModal()

  // Get all category names
  const categories = ['All', ...servicesData.categories.map(cat => cat.name)]

  // Toggle service description expansion
  const toggleExpand = (serviceId: string) => {
    setExpandedServices(prev => {
      const newSet = new Set(prev)
      if (newSet.has(serviceId)) {
        newSet.delete(serviceId)
      } else {
        newSet.add(serviceId)
      }
      return newSet
    })
  }

  // Filter services based on search and category
  const filteredCategories = useMemo(() => {
    let filtered = servicesData.categories

    // Filter by category
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(cat => cat.name === selectedCategory)
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.map(cat => ({
        ...cat,
        services: cat.services.filter(service =>
          service.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          service.description.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(cat => cat.services.length > 0)
    }

    return filtered
  }, [searchQuery, selectedCategory])

  return (
    <div className="min-h-screen bg-white dark:bg-custom-dark text-gray-900 dark:text-gray-100 transition-colors">
      {/* SEO Meta Tags */}
      <SEO 
        title="Our Barber Services | Fades, Beards, Shaves - Barber Innovations Roselle NJ"
        description="Explore Barber Innovations' full range of premium barber services including classic fades, modern beard trims, and traditional straight razor shaves in Roselle, NJ. Book your appointment today!"
        canonical="https://www.barberinnovations.com/services-v2"
        keywords="skin fade haircut roselle nj, beard trim lineup roselle, hot towel shave roselle nj, kids haircut roselle, vip barber service roselle, barber services nj"
      />

      {/* QR Code Modal */}
      <QRCodeModal 
        isOpen={isOpen} 
        onClose={closeModal} 
      />

      {/* Hero Section */}
      <motion.section
        className="pt-28 pb-12 px-4 sm:px-6 lg:px-8 text-center"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="mx-auto max-w-5xl">
          <h1 className="font-display font-bold text-[clamp(2.5rem,6vw,4.5rem)] leading-tight text-gray-900 dark:text-white">
            {servicesData.title}
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 mt-4">
            {servicesData.subtitle}
          </p>
        </div>
      </motion.section>

      {/* Search and Filter Section */}
      <section className="px-4 sm:px-6 lg:px-8 pb-8">
        <div className="mx-auto max-w-6xl">
          {/* Search Bar */}
          <div className="mb-8">
            <div className="relative">
              <input
                type="text"
                placeholder="Search services..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-6 py-4 pl-12 rounded-full bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-primary text-gray-900 dark:text-white"
              />
              <svg className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          {/* Category Navigation */}
          <div className="flex flex-wrap gap-3 justify-center mb-12">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full font-semibold transition-all ${
                  selectedCategory === category
                    ? 'bg-primary text-black shadow-lg'
                    : 'bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Services by Category */}
      <section className="px-4 sm:px-6 lg:px-8 pb-20">
        <div className="mx-auto max-w-6xl space-y-16">
          {filteredCategories.map((category, index) => (
            <motion.div
              key={category.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              {/* Category Title */}
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-left">
                {category.name}
              </h2>

              {/* Services Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {category.services.map((service) => {
                  const isExpanded = expandedServices.has(service.id)
                  const hasLongDescription = service.description && service.description.length > 100
                  
                  return (
                    <div
                      key={service.id}
                      className="rounded-2xl p-6 border border-gray-700/30 shadow-xl hover:shadow-2xl transition-all duration-300 flex flex-col"
                      style={{ backgroundColor: '#252527' }}
                    >
                      {/* Content Section - Grows to fill space */}
                      <div className="flex-1">
                        {/* Service Name */}
                        <h3 className="text-lg font-bold text-white leading-tight mb-3">
                          {service.name}
                        </h3>

                        {/* Description (expandable) */}
                        {service.description && (
                          <div className="mb-4">
                            <p className={`text-sm text-gray-400 leading-relaxed ${!isExpanded && hasLongDescription ? 'line-clamp-2' : ''}`}>
                              {service.description}
                            </p>
                            {hasLongDescription && (
                              <button
                                onClick={() => toggleExpand(service.id)}
                                className="text-primary text-sm font-semibold mt-2 hover:underline"
                              >
                                {isExpanded ? 'Show Less' : 'Read More'}
                              </button>
                            )}
                          </div>
                        )}

                        {/* Price & Duration */}
                        <div className="flex items-center gap-4">
                          <span className="text-2xl font-bold text-primary">{service.price}</span>
                          <div className="flex items-center gap-2">
                            <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-sm text-gray-400">{service.duration}</span>
                          </div>
                        </div>
                      </div>

                      {/* Book Now Button - Always at Bottom, Full Width Edge to Edge */}
                      <button
                        onClick={openModal}
                        className="-mx-6 -mb-6 mt-4 bg-primary text-black py-3 px-4 rounded-b-2xl font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        <span>Book Now</span>
                      </button>
                    </div>
                  )
                })}
              </div>
            </motion.div>
          ))}

          {/* No Results */}
          {filteredCategories.length === 0 && (
            <div className="text-center py-20">
              <p className="text-2xl text-gray-500 dark:text-gray-400">
                No services found matching "{searchQuery}"
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  )
}

export default ServicesPageV2
